import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import type {
  CapitalAnalyticsSummary,
  ControlCentreOverview,
  MarginGranularity,
  MarginPeriod,
  MaturitySeverity,
  MaturityWarning,
  PartnerCapitalDeployment,
  PartnerControlSnapshot,
} from '../../models/CapitalAnalyticsTypes';

type ApiEnvelope<T> = { data: T; message?: string; statusCode: number };

const BASE = `${LEAD_API_PREFIX}/capitals/analytics`;

export interface PartnerLendingAllocation {
  id?: string;
  capitalPartnerId?: string;
  partnerName?: string;
  debitId: string;
  borrowerId?: string;
  borrowerName?: string;
  borrowerCity?: string;
  borrowerRatePctMonthly?: number;
  allocatedAmount: number;
  partnerRatePctMonthly?: number;
  note?: string;
  allocatedAt?: string;
  debitMaturityDate?: string;
  debitStatus?: string;
  debitType?: string;
  debitAmount?: number;
  debitOutstanding?: number;
  monthlyPartnerIncome?: number;
  totalPaidOut?: number;
  lastPayoutDate?: string;
}

export interface PartnerLendingPayout {
  id?: string;
  capitalPartnerId?: string;
  partnerName?: string;
  lendingAllocationId: string;
  debitId?: string;
  borrowerName?: string;
  debitType?: string;
  amount: number;
  payoutDate: string;
  periodKey: string;
  referenceNote?: string;
}

export interface PartnerLendingPayoutSummary {
  monthlyAccruedIncome: number;
  totalPaidOut: number;
  outstandingBalance: number;
  linkedDebitCount: number;
  payoutCount: number;
}

export async function getControlCentreOverview(): Promise<ControlCentreOverview> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/control-centre`,
  });
  return (response.data as ApiEnvelope<ControlCentreOverview>).data;
}

export async function getPartnerControlSnapshot(partnerId: string): Promise<PartnerControlSnapshot | null> {
  if (!partnerId?.trim()) return null;
  try {
    const response = await HttpClient.GET({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/snapshot`,
    });
    return (response.data as ApiEnvelope<PartnerControlSnapshot>).data;
  } catch {
    return null;
  }
}

export async function getCapitalAnalyticsSummary(): Promise<CapitalAnalyticsSummary> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/summary`,
  });
  return (response.data as ApiEnvelope<CapitalAnalyticsSummary>).data;
}

export async function listPartnerCapitalDeployments(opts?: {
  partnerId?: string;
  use?: 'LENDING' | 'EQUITY';
}): Promise<PartnerCapitalDeployment[]> {
  const params: Record<string, string> = {};
  if (opts?.partnerId) params.partnerId = opts.partnerId;
  if (opts?.use) params.use = opts.use;
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/deployments`,
    params,
  });
  return (response.data as ApiEnvelope<PartnerCapitalDeployment[]>).data ?? [];
}

export async function listCapitalMarginPeriods(
  granularity: MarginGranularity,
): Promise<MarginPeriod[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/margin`,
    params: { granularity },
  });
  return (response.data as ApiEnvelope<MarginPeriod[]>).data ?? [];
}

export async function listCapitalMaturityWarnings(opts?: {
  severity?: MaturitySeverity;
}): Promise<MaturityWarning[]> {
  const params: Record<string, string> = {};
  if (opts?.severity) params.severity = opts.severity;
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/maturity-warnings`,
    params,
  });
  return (response.data as ApiEnvelope<MaturityWarning[]>).data ?? [];
}

export async function listPartnerLendingAllocations(partnerId: string): Promise<PartnerLendingAllocation[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-allocations`,
  });
  return (response.data as ApiEnvelope<PartnerLendingAllocation[]>).data ?? [];
}

export async function addPartnerLendingAllocation(
  partnerId: string,
  body: PartnerLendingAllocation,
): Promise<PartnerLendingAllocation> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-allocations`,
    data: body,
  });
  return (response.data as ApiEnvelope<PartnerLendingAllocation>).data;
}

export async function deletePartnerLendingAllocation(
  partnerId: string,
  allocationId: string,
): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-allocations/${encodeURIComponent(allocationId)}`,
  });
}

/** Payload row when creating a debit with partner capital. */
export interface CreateDebitPartnerAllocation {
  partnerId: string;
  allocatedAmount: number;
  partnerRatePctMonthly?: number;
  note?: string;
}

export async function listLendingAllocationsForBorrower(borrowerId: string): Promise<PartnerLendingAllocation[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/lending-allocations`,
    params: { borrowerId },
  });
  return (response.data as ApiEnvelope<PartnerLendingAllocation[]>).data ?? [];
}

export async function listPartnerLendingPayouts(partnerId: string): Promise<PartnerLendingPayout[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-payouts`,
  });
  return (response.data as ApiEnvelope<PartnerLendingPayout[]>).data ?? [];
}

export async function getPartnerLendingPayoutSummary(partnerId: string): Promise<PartnerLendingPayoutSummary> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-payout-summary`,
  });
  return (response.data as ApiEnvelope<PartnerLendingPayoutSummary>).data;
}

export async function addPartnerLendingPayout(
  partnerId: string,
  body: PartnerLendingPayout,
): Promise<PartnerLendingPayout> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-payouts`,
    data: body,
  });
  return (response.data as ApiEnvelope<PartnerLendingPayout>).data;
}

export async function deletePartnerLendingPayout(partnerId: string, payoutId: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/partners/${encodeURIComponent(partnerId)}/lending-payouts/${encodeURIComponent(payoutId)}`,
  });
}

/** Partner portal — lending/control snapshot for logged-in partner. */
export async function getPartnerPortalControlSnapshot(): Promise<PartnerControlSnapshot> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/partner/control-snapshot`,
  });
  return (response.data as ApiEnvelope<PartnerControlSnapshot>).data;
}

export async function getPartnerPortalLendingAllocations(): Promise<PartnerLendingAllocation[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/partner/lending-allocations`,
  });
  return (response.data as ApiEnvelope<PartnerLendingAllocation[]>).data ?? [];
}

export async function getPartnerPortalLendingPayouts(): Promise<PartnerLendingPayout[]> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/partner/lending-payouts`,
  });
  return (response.data as ApiEnvelope<PartnerLendingPayout[]>).data ?? [];
}

export async function getPartnerPortalLendingPayoutSummary(): Promise<PartnerLendingPayoutSummary> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/partner/lending-payout-summary`,
  });
  return (response.data as ApiEnvelope<PartnerLendingPayoutSummary>).data;
}
